/**
 * api/endpoints.ts
 * ================
 *
 * Strongly-typed wrappers for every API surface. Naming mirrors the
 * Django URL routers so a developer can find an endpoint by feature
 * (search "patient" → patient.* helpers).
 *
 * Each helper returns a Promise<T> and is composable with TanStack Query.
 */

import { api } from './client';
import type {
  AIAssistanceEvent,
  AppointmentDetail,
  AppointmentListItem,
  CarePackage,
  ClinicianProfile,
  ConsentReceipt,
  Encounter,
  LabOrder,
  LongitudinalRecord,
  MarketplaceSearchResult,
  MedicalConsent,
  MedicalProcedure,
  MembershipPlan,
  PatientProfile,
  Prescription,
  Referral,
  SickNote,
  UUID,
  VerifiableHealthCredential,
} from '@/types/medical';

// Auth (the Django session login is handled by Django pages — we only need
// "who am I" for the SPA).
export const auth = {
  whoami: () => api.get<{
    is_authenticated: boolean;
    user_id: number | null;
    email: string;
    user_type: 'guest' | 'employee' | 'owner' | 'superuser' | null;
    has_patient_profile: boolean;
    has_clinician_profile: boolean;
    can_administer_practice: boolean;
    permissions: string[];
  }>('/api/medical/whoami/'),
};

// ─────────────────────────────────────────────────────────────────────────────
// Patient surface
// ─────────────────────────────────────────────────────────────────────────────

export const patient = {
  me: {
    get: () => api.get<PatientProfile>('/api/medical/patient/me/'),
    update: (body: Partial<PatientProfile>) =>
      api.patch<PatientProfile>('/api/medical/patient/me/0/', body),
  },
  appointments: {
    list: () => api.get<AppointmentListItem[]>('/api/medical/patient/appointments/'),
    get: (id: UUID) => api.get<AppointmentDetail>(`/api/medical/patient/appointments/${id}/`),
    cancel: (id: UUID, reason: string) =>
      api.post<{ status: string }>(`/api/medical/patient/appointments/${id}/cancel/`, { reason }),
    checkIn: (id: UUID) =>
      api.post<{ status: string; video_room_id: string }>(
        `/api/medical/patient/appointments/${id}/check-in/`,
      ),
  },
  prescriptions: {
    list: () => api.get<Prescription[]>('/api/medical/patient/prescriptions/'),
    get: (id: UUID) => api.get<Prescription>(`/api/medical/patient/prescriptions/${id}/`),
    qr: (id: UUID) => api.get<{
      rx_id: string; qr_payload: string; registry_external_id: string;
      valid_until: string | null;
    }>(`/api/medical/patient/prescriptions/${id}/qr/`),
    pdf: (id: UUID) => api.get<{ url: string }>(`/api/medical/patient/prescriptions/${id}/pdf/`),
    sharePharmacy: (id: UUID, email: string) =>
      api.post<{ status: string }>(`/api/medical/patient/prescriptions/${id}/share-pharmacy/`,
        { email }),
  },
  sickNotes:  { list: () => api.get<SickNote[]>('/api/medical/patient/sick-notes/') },
  labOrders:  { list: () => api.get<LabOrder[]>('/api/medical/patient/lab-orders/') },
  referrals:  { list: () => api.get<Referral[]>('/api/medical/patient/referrals/') },
  record:     { get:  () => api.get<LongitudinalRecord>('/api/medical/patient/record/') },
  consents: {
    list: () => api.get<MedicalConsent[]>('/api/medical/patient/consents/'),
    grant: (consent_type: string, granted: boolean, policy_template_id?: number) =>
      api.post<MedicalConsent>('/api/medical/patient/consents/',
        { consent_type, granted, policy_template_id }),
    revoke: (id: number, reason?: string) =>
      api.post<MedicalConsent>(`/api/medical/patient/consents/${id}/revoke/`, { reason }),
  },
  wallet: {
    credentials: {
      list: () => api.get<VerifiableHealthCredential[]>(
        '/api/medical/patient/wallet/credentials/'),
    },
    receipts: {
      list:   () => api.get<ConsentReceipt[]>('/api/medical/wallet/consent-receipts/'),
      verify: (id: number) =>
        api.post<{ valid: boolean }>(`/api/medical/wallet/consent-receipts/${id}/verify/`),
    },
  },
  carePathways: {
    list: () => api.get<unknown[]>('/api/medical/patient/care-pathways/'),
  },
  devices: {
    list:   () => api.get<unknown[]>('/api/medical/patient/devices/'),
    connect: (provider: string, device_type: string) =>
      api.post<{ oauth_url: string }>('/api/medical/patient/devices/',
        { provider, device_type }),
    revoke: (id: number) =>
      api.post<unknown>(`/api/medical/patient/devices/${id}/revoke/`),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Clinician surface
// ─────────────────────────────────────────────────────────────────────────────

export const clinician = {
  me: {
    get:    () => api.get<ClinicianProfile>('/api/medical/clinician/me/'),
    update: (body: Partial<ClinicianProfile>) =>
      api.patch<ClinicianProfile>('/api/medical/clinician/me/0/', body),
  },
  appointments: {
    list: (params?: { status?: string; date?: string }) =>
      api.get<AppointmentListItem[]>('/api/medical/clinician/appointments/',
        { params }),
    get: (id: UUID) =>
      api.get<AppointmentDetail>(`/api/medical/clinician/appointments/${id}/`),
    start: (id: UUID) =>
      api.post<Encounter>(`/api/medical/clinician/appointments/${id}/start/`),
    complete: (id: UUID, follow_up_days?: number) =>
      api.post<{ status: string }>(
        `/api/medical/clinician/appointments/${id}/complete/`, { follow_up_days }),
  },
  encounters: {
    get:    (id: number) => api.get<Encounter>(`/api/medical/clinician/encounters/${id}/`),
    update: (id: number, body: Partial<Encounter>) =>
      api.patch<Encounter>(`/api/medical/clinician/encounters/${id}/`, body),
    lock:   (id: number) =>
      api.post<Encounter>(`/api/medical/clinician/encounters/${id}/lock/`),
    addAddendum: (id: number, text: string) =>
      api.post<{ id: number; created_at: string }>(
        `/api/medical/clinician/encounters/${id}/addendum/`, { text }),
  },
  prescriptions: {
    list:   () => api.get<Prescription[]>('/api/medical/clinician/prescriptions/'),
    create: (payload: unknown) =>
      api.post<Prescription>('/api/medical/clinician/prescriptions/', payload),
    check:  (id: UUID) =>
      api.post<{ overall_severity: string; findings: unknown[]; check_id: number }>(
        `/api/medical/clinician/prescriptions/${id}/check/`),
    sign:   (id: UUID, ai_event_id?: UUID) =>
      api.post<Prescription>(`/api/medical/clinician/prescriptions/${id}/sign/`,
        { ai_event_id }),
    revoke: (id: UUID, reason: string) =>
      api.post<Prescription>(`/api/medical/clinician/prescriptions/${id}/revoke/`,
        { reason }),
    amend:  (id: UUID, payload: unknown) =>
      api.post<Prescription>(`/api/medical/clinician/prescriptions/${id}/amend/`,
        payload),
  },
  sickNotes:  {
    create: (payload: Partial<SickNote>) =>
      api.post<SickNote>('/api/medical/clinician/sick-notes/', payload),
  },
  ai: {
    suggest: (capability: string, inputs: Record<string, unknown>,
              ctx?: { patient_id?: number; appointment_id?: UUID }) =>
      api.post<AIAssistanceEvent>('/api/medical/clinician/ai-suggestions/',
        { capability, inputs, ...ctx }),
    decide:  (event_id: UUID, action: 'accepted' | 'edited' | 'rejected' | 'escalated',
              edited_output?: unknown, reason?: string) =>
      api.post<AIAssistanceEvent>(
        `/api/medical/clinician/ai-suggestions/${event_id}/decision/`,
        { action, edited_output, reason }),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Public marketplace surface
// ─────────────────────────────────────────────────────────────────────────────

export const marketplace = {
  search: (params: Record<string, string | number | string[]>) =>
    api.get<MarketplaceSearchResult>('/api/medical/marketplace/search/', { params }),
  clinicians: {
    get: (slug: string) =>
      api.get<unknown>(`/api/medical/marketplace/clinicians/${slug}/`),
    rankExplanation: (slug: string) =>
      api.get<unknown>(`/api/medical/marketplace/clinicians/${slug}/rank-explanation/`),
  },
  procedures: {
    forClinician: (clinicianId: number) =>
      api.get<MedicalProcedure[]>(`/api/medical/marketplace/clinicians/${clinicianId}/procedures/`),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Commerce
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Commerce (patient-facing)
// ─────────────────────────────────────────────────────────────────────────────

export const commerce = {
  packages: {
    list:     () => api.get<CarePackage[]>('/api/medical/commerce/packages/'),
    purchase: (id: number, patient_id?: number) =>
      api.post<unknown>(`/api/medical/commerce/packages/${id}/purchase/`, { patient_id }),
  },
  memberships: {
    plans: () => api.get<MembershipPlan[]>('/api/medical/commerce/membership-plans/'),
    list:  () => api.get<unknown[]>('/api/medical/commerce/memberships/'),
    cancel: (id: UUID, reason?: string) =>
      api.post<unknown>(`/api/medical/commerce/memberships/${id}/cancel/`, { reason }),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Practice admin surface
// ─────────────────────────────────────────────────────────────────────────────

export const practice = {
  me: {
    get:    () => api.get<unknown>('/api/medical/practice/me/'),
    update: (body: Record<string, unknown>) =>
      api.patch<unknown>('/api/medical/practice/me/0/', body),
  },
  clinicians: {
    list:    () => api.get<unknown[]>('/api/medical/practice/clinicians/'),
    invite:  (payload: { email: string; display_name: string; specialty_id?: number }) =>
      api.post<unknown>('/api/medical/practice/clinicians/invite/', payload),
    verify:  (id: number, status: 'approved' | 'rejected', reason?: string) =>
      api.post<unknown>(`/api/medical/practice/clinicians/${id}/verify/`,
        { status, reason }),
    deactivate: (id: number, reason: string) =>
      api.post<unknown>(`/api/medical/practice/clinicians/${id}/deactivate/`, { reason }),
  },
  procedures: {
    list:    () => api.get<unknown[]>('/api/medical/practice/procedures/'),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/procedures/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/procedures/${id}/`, body),
    delete:  (id: number) =>
      api.delete<unknown>(`/api/medical/practice/procedures/${id}/`),
  },
  intakeTemplates: {
    list:    () => api.get<unknown[]>('/api/medical/practice/intake-templates/'),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/intake-templates/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/intake-templates/${id}/`, body),
    delete:  (id: number) =>
      api.delete<unknown>(`/api/medical/practice/intake-templates/${id}/`),
  },
  appointments: {
    list:    (params?: { status?: string; date?: string }) =>
      api.get<unknown[]>('/api/medical/practice/appointments/', { params }),
  },
  reviews: {
    list:    () => api.get<unknown[]>('/api/medical/practice/reviews/'),
    moderate: (id: number, action: 'approve' | 'reject' | 'flag', reason?: string) =>
      api.post<unknown>(`/api/medical/practice/reviews/${id}/moderate/`,
        { action, reason }),
  },
  carePackages: {
    list:    () => api.get<unknown[]>('/api/medical/practice/care-packages/'),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/care-packages/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/care-packages/${id}/`, body),
  },
  membershipPlans: {
    list:    () => api.get<unknown[]>('/api/medical/practice/membership-plans/'),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/membership-plans/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/membership-plans/${id}/`, body),
  },
  payerContracts: {
    list:    () => api.get<unknown[]>('/api/medical/practice/payer-contracts/'),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/payer-contracts/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/payer-contracts/${id}/`, body),
  },
  interop: {
    connections: {
      list:    () => api.get<unknown[]>('/api/medical/practice/interop-connections/'),
      create:  (payload: Record<string, unknown>) =>
        api.post<unknown>('/api/medical/practice/interop-connections/', payload),
      revoke:  (id: number) =>
        api.post<unknown>(`/api/medical/practice/interop-connections/${id}/revoke/`),
    },
  },
  availability: {
    list:    (clinicianId: number) =>
      api.get<unknown[]>('/api/medical/practice/availability/', { params: { clinician: clinicianId } }),
    create:  (payload: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/practice/availability/', payload),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/practice/availability/${id}/`, body),
    delete:  (id: number) =>
      api.delete<unknown>(`/api/medical/practice/availability/${id}/`),
  },
};

export const aiSafety = {
  models: {
    list:    () => api.get<unknown[]>('/api/medical/ai-safety/models/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/ai-safety/models/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/ai-safety/models/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/ai-safety/models/${id}/`, body),
    approve: (id: number, reason?: string) =>
      api.post<unknown>(`/api/medical/ai-safety/models/${id}/approve/`, { reason }),
  },
  capabilities: {
    list:    () => api.get<unknown[]>('/api/medical/ai-safety/capabilities/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/ai-safety/capabilities/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/ai-safety/capabilities/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/ai-safety/capabilities/${id}/`, body),
    activate: (id: number) =>
      api.post<unknown>(`/api/medical/ai-safety/capabilities/${id}/activate/`),
    retire: (id: number, reason: string) =>
      api.post<unknown>(`/api/medical/ai-safety/capabilities/${id}/retire/`, { reason }),
  },
  safetyCases: {
    list:    () => api.get<unknown[]>('/api/medical/ai-safety/safety-cases/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/ai-safety/safety-cases/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/ai-safety/safety-cases/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/ai-safety/safety-cases/${id}/`, body),
    approve: (id: number, reason?: string) =>
      api.post<unknown>(`/api/medical/ai-safety/safety-cases/${id}/approve/`, { reason }),
  },
  events: {
    list:    (params?: { capability?: string; human_action?: string }) =>
      api.get<unknown[]>('/api/medical/ai-safety/events/', { params }),
    stats:   () => api.get<unknown>('/api/medical/ai-safety/events/stats/'),
  },
};
export const regOps = {
  jurisdictions: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/jurisdictions/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/regulation-ops/jurisdictions/${id}/`),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/regulation-ops/jurisdictions/${id}/`, body),
    launchReadiness: (id: number) =>
      api.get<unknown>(`/api/medical/regulation-ops/jurisdictions/${id}/launch-readiness/`),
  },
  sources: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/sources/'),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/regulation-ops/sources/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/regulation-ops/sources/${id}/`, body),
  },
  rules: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/rules/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/regulation-ops/rules/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/regulation-ops/rules/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/regulation-ops/rules/${id}/`, body),
  },
  releases: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/releases/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/regulation-ops/releases/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/regulation-ops/releases/', body),
    runTests: (id: number) =>
      api.post<unknown>(`/api/medical/regulation-ops/releases/${id}/run-tests/`),
    approve: (id: number, reason?: string) =>
      api.post<unknown>(`/api/medical/regulation-ops/releases/${id}/approve/`, { reason }),
    activate: (id: number) =>
      api.post<unknown>(`/api/medical/regulation-ops/releases/${id}/activate/`),
    rollback: (id: number, reason: string) =>
      api.post<unknown>(`/api/medical/regulation-ops/releases/${id}/rollback/`, { reason }),
  },
  testCases: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/test-cases/'),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/regulation-ops/test-cases/', body),
    update:  (id: number, body: Record<string, unknown>) =>
      api.patch<unknown>(`/api/medical/regulation-ops/test-cases/${id}/`, body),
  },
  changeRequests: {
    list:    () => api.get<unknown[]>('/api/medical/regulation-ops/change-requests/'),
    get:     (id: number) =>
      api.get<unknown>(`/api/medical/regulation-ops/change-requests/${id}/`),
    create:  (body: Record<string, unknown>) =>
      api.post<unknown>('/api/medical/regulation-ops/change-requests/', body),
    transition: (id: number, status: string, notes?: string) =>
      api.post<unknown>(`/api/medical/regulation-ops/change-requests/${id}/transition/`,
        { status, notes }),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// AI Safety surface (Pillar 10)
// ─────────────────────────────────────────────────────────────────────────────

